import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Input } from "reactstrap";
import axios from "./axios-config";
import swal from "sweetalert";
import { FaDownload, FaFilePdf, FaSearch, FaFileAlt, FaCheckSquare, FaUniversity } from "react-icons/fa";
import { useIdioma } from "./context/IdiomaContext";

const ALL = "ALL";

const DocumentosPoliza = () => {
  const { t } = useIdioma();
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [tipoDocumentoSeleccionado, setTipoDocumentoSeleccionado] = useState("");
  const [documentosPoliza, setDocumentosPoliza] = useState([]);
  const [cargandoDocumentos, setCargandoDocumentos] = useState(false);
  const [conteosDocumentos, setConteosDocumentos] = useState({});
  const [searchText, setSearchText] = useState("");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`,
    }),
    []
  );

  useEffect(() => {
    const idPoliza = window.idPoliza;
    if (!idPoliza) {
      // Si aún no está listo, se podría reintentar o evitar fallo, pero esto sigue el mismo patrón
      return;
    }

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/it-api-clientes2/rs/operaciones/getTiposDocumentosPoliza/${idPoliza}`,
        { headers: authHeaders }
      )
      .then((response) => {
        const data = response?.data?.data ?? response?.data ?? [];
        setTiposDocumento(Array.isArray(data) ? data : []);
      })
      .catch(() => setTiposDocumento([]));
  }, [authHeaders]);

  const totalDocs = Object.values(conteosDocumentos).reduce((acc, val) => acc + (val || 0), 0);

  const downloadDocumento = async (doc) => {
    console.log("Documento: ", doc);
    if (doc?.url) {
      window.open(doc.url, "_blank");
      return;
    }
    if (doc?.base64) {
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${doc.base64}`;
      a.download = doc?.nombre || "documento.pdf";
      a.click();
      return;
    }

    const ideObj = doc?.ideObj || doc?.ide_obj;
    if (ideObj) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/it-api-clientes2/rs/operaciones/documentos/descargar/${ideObj}`,
          {
            headers: authHeaders,
            responseType: "blob", // Permite descarga segura con Auth Header
          }
        );

        let fileName = doc?.desObj || doc?.nombre || `documento_${ideObj}`;
        const ext = doc?.extTip || doc?.extension;
        if (ext && !fileName.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
          fileName += `.${ext}`;
        }

        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition && contentDisposition.includes("filename=")) {
          const matches = contentDisposition.match(/filename="?([^"]+)"?/);
          if (matches && matches[1]) {
            fileName = matches[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error("Error al descargar documento: ", error);
        swal("Error", "No se pudo descargar el documento.", "error");
      }
      return;
    }

    swal("Aviso", "Este documento no tiene identificador válido para descargar.", "info");
  };

  const cargarDocumentosPoliza = async (codigoTipoDocumento) => {
    const idPoliza = window.idPoliza;
    if (!idPoliza) {
      swal("Aviso", "Aún no se ha cargado la póliza. Intenta nuevamente.", "info");
      return;
    }

    setTipoDocumentoSeleccionado(codigoTipoDocumento);
    setCargandoDocumentos(true);
    setDocumentosPoliza([]);

    try {
      if (codigoTipoDocumento === ALL) {
        // Carga todos los tipos y los concatena (para emular "All Documents")
        const requests = tiposDocumento.map((t) => {
          const codigo = t?.codigo ?? t;
          return axios.get(
            `${process.env.REACT_APP_API_URL}/it-api-clientes2/rs/operaciones/getDocumentosPoliza`,
            {
              params: { idPoliza, codigoTipoDocumento: codigo },
              headers: authHeaders,
            }
          );
        });

        const responses = await Promise.all(requests);

        const docsFlatten = [];
        const conteos = {};

        responses.forEach((response, i) => {
          const codigo = (tiposDocumento[i]?.codigo ?? tiposDocumento[i]) ?? `T${i}`;
          console.log("Respuesta: ", response);
          const data = response?.data?.documentos ?? response.data.documentos ?? [];
          const docs = Array.isArray(data) ? data : [];
          conteos[codigo] = docs.length;
          docsFlatten.push(...docs);
        });

        setConteosDocumentos(conteos);
        setDocumentosPoliza(docsFlatten);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/it-api-clientes2/rs/operaciones/getDocumentosPoliza`,
        {
          params: {
            idPoliza,
            codigoTipoDocumento,
          },
          headers: authHeaders,
        }
      );
      console.log("Respuesta: ", response);
      const data = response?.data?.documentos ?? response.data.documentos ?? [];
      const docs = Array.isArray(data) ? data : [];

      setDocumentosPoliza(docs);
      setConteosDocumentos((prev) => ({ ...prev, [codigoTipoDocumento]: docs.length }));
    } catch (e) {
      swal("Error", "Ocurrió un error al consultar los documentos de la póliza.", "error");
      setDocumentosPoliza([]);
      setConteosDocumentos((prev) => ({ ...prev, [codigoTipoDocumento]: 0 }));
    } finally {
      setCargandoDocumentos(false);
    }
  };

  const tipoSeleccionado = tiposDocumento.find((t) => (t?.codigo ?? t) === tipoDocumentoSeleccionado);
  const tituloSeccion =
    tipoDocumentoSeleccionado === ALL
      ? "All Documents"
      : tipoSeleccionado
        ? tipoSeleccionado.descripcion ?? tipoSeleccionado.codigo ?? "Documentos"
        : "Documentos";

  const documentosFiltrados = documentosPoliza.filter((d) => {
    const nombre = (d?.nombre || "").toLowerCase();
    const q = (searchText || "").toLowerCase().trim();
    return !q || nombre.includes(q);
  });

  return (
    <div className="bg-white rounded-4 shadow-sm border p-4">
      {/* Header Archive Library */}
      <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 mb-3">
        <div>
          <h4 className="mb-1 fw-bold" style={{ letterSpacing: "-0.01em" }}>

          </h4>
          <div className="text-muted" style={{ fontSize: "0.85rem", maxWidth: 560 }}>
            {t("Administra tus documentos de póliza de forma sencilla. Todos tus archivos, organizados y seguros.")}
          </div>
        </div>

        <div className="w-100 w-md-200" style={{ maxWidth: 360 }}>
          <div className="input-group">
            <span className="input-group-text bg-light border-0">
              <FaSearch className="text-muted" />
            </span>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t("Buscar en documentos...")}
              className="border-0 shadow-none"
            />
          </div>
        </div>
      </div>

      {/* Pills header */}
      <div className="mb-2 fw-semibold text-secondary" style={{ fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {t("Tipos de documento disponibles")}
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap mb-4">
        {/* 
        <button
          type="button"
          className={[
            "btn btn-sm",
            tipoDocumentoSeleccionado === ALL ? "btn-primary" : "btn-light border",
          ].join(" ")}
          onClick={() => cargarDocumentosPoliza(ALL)}
        >
          Todos los documentos{" "}
          <Badge
            pill
            style={{
              marginLeft: 8,
              backgroundColor: tipoDocumentoSeleccionado === ALL ? "rgba(255,255,255,0.18)" : "#e9ecef",
              color: tipoDocumentoSeleccionado === ALL ? "#fff" : "#212529",
            }}
          >
            {totalDocs}
          </Badge>
        </button>
        */}

        {tiposDocumento.map((t, idx) => {
          const codigo = t?.codigo ?? t;
          const desc = t?.descripcion ?? t?.codigo ?? t;
          const activo = codigo === tipoDocumentoSeleccionado;
          const count = conteosDocumentos[codigo];

          const getIcon = () => {
            const lower = desc.toLowerCase();
            if (lower.includes("auditor")) return <FaCheckSquare className="me-2 mb-1" />;
            if (lower.includes("finan")) return <FaUniversity className="me-2 mb-1" />;
            return <FaFileAlt className="me-2 mb-1" />;
          };

          return (
            <button
              key={codigo ?? idx}
              type="button"
              style={{ backgroundColor: "rgb(0, 52, 121)", color: "#fff" }}
              className={["btn btn-sm d-flex align-items-center justify-content-center", activo ? "btn-primary" : "btn-light border"].join(" ")}
              onClick={() => cargarDocumentosPoliza(codigo)}
            >
              {getIcon()}
              <span>{desc}</span>
              {typeof count === "number" && (
                <Badge
                  pill
                  style={{
                    marginLeft: 8,
                    backgroundColor: activo ? "rgba(255,255,255,0.18)" : "#e9ecef",
                    color: activo ? "#fff" : "#212529",
                  }}
                >
                  {count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* Recent Archive header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-primary" style={{ fontSize: 18 }}>
            ●
          </span>
          <span className="fw-semibold">{t("Documentos recientes")}</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="text-muted small" style={{ fontWeight: 600 }}>
            {t("Mostrando")} {documentosFiltrados.length} {t(documentosFiltrados.length === 1 ? "documento" : "documentos")}
          </div>
        </div>
      </div>

      {/* Cards list */}
      {cargandoDocumentos ? (
        <div className="text-muted small">{t("Cargando documentos...")}</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {documentosFiltrados.map((doc, idx) => {
            const nombre = doc?.nombre || "Documento";
            const urg = (nombre || "").toLowerCase().includes("urg");
            // (Sin badge ACTIVE/URGENT para mantener el diseño limpio)

            return (
              <div
                key={idx}
                className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 bg-light rounded-4 p-3"
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{ width: 44, height: 44, backgroundColor: "rgba(220,38,38,0.10)" }}
                  >
                    <FaFilePdf className="text-danger" />
                  </div>
                  <div>
                    <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>
                      {nombre}
                    </div>
                    <div className="text-muted small" style={{ fontSize: "0.78rem" }}>
                      PDF • {tituloSeccion}
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3">
                  <Button style={{ backgroundColor: "rgb(0, 52, 121)" }} className="px-4" size="sm" onClick={() => downloadDocumento(doc)}>
                    <FaDownload style={{ marginRight: 8 }} />
                    {t("Descargar")}
                  </Button>
                </div>
              </div>
            );
          })}

          {!tipoDocumentoSeleccionado ? (
            <div className="text-muted small">
              {t("Selecciona un tipo de documento para ver sus archivos.")}
            </div>
          ) : !documentosFiltrados.length ? (
            <div className="text-muted small">
              {t("No hay archivos disponibles.")}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default DocumentosPoliza;

